import moment from 'moment';
import { formatLongDate } from './dates';
import { DATE_FORMAT } from './datetime';

/**
 * Trigger a browser download for a Blob. Shared by the client-side CSV
 * builders below and the server-side Reports export (the service builds the
 * .xlsx; the browser only saves it — see reports-export.controller.js).
 *
 * @param {Blob} blob
 * @param {string} filename Full file name including extension.
 * @returns {boolean} false when the browser cannot trigger downloads.
 */
export const downloadBlob = (blob, filename) => {
    if (typeof document === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
        return false;
    }
    const link = document.createElement('a');
    if (link.download === undefined) return false;

    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (typeof URL.revokeObjectURL === 'function') {
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }
    return true;
};

// Convert array of objects to CSV format
export const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
        alert('No data to export');
        return;
    }

    // Get headers from first object
    const headers = Object.keys(data[0]);

    // Create CSV content
    const csvContent = [
        headers.join(','), // Header row
        ...data.map((row) =>
            headers
                .map((header) => {
                    let value = row[header];

                    // Handle null/undefined values
                    if (value === null || value === undefined) {
                        value = '';
                    }

                    // Format dates
                    if (header.includes('date') || header.includes('created_at') || header.includes('scheduled_for')) {
                        value = moment(value).format(DATE_FORMAT);
                    }

                    // Handle values that contain commas or quotes
                    if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                        value = `"${value.replace(/"/g, '""')}"`;
                    }

                    return value;
                })
                .join(',')
        ),
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `${filename}_${moment().format('YYYY-MM-DD')}.csv`);
};

// Specific export functions for each screen
export const exportCohortUsersToCSV = (users) => {
    const formattedData = users.map((user) => ({
        Name: user.name,
        'Last Active': formatLongDate(user.last_active),
        'User ID': user.user_id,
    }));

    exportToCSV(formattedData, 'cohort_users_export');
};

export const exportSessionEventsToCSV = (sessions) => {
    const formattedData = sessions.map((session) => ({
        Name: session.name || 'Unknown user',
        'Session Start': session.date,
        Platform: session.platform || '—',
        Type: session.isNew ? 'New' : 'Returning',
        'User ID': session.user_id,
    }));

    exportToCSV(formattedData, 'session_events_export');
};

/**
 * The routed /ledger page's export (`components/ledger/elements/LedgerTableActions.js`),
 * unchanged: five columns over the legacy row shape. The Reports tabs export
 * server-side (`reports-export.controller.js`) and no longer have a CSV writer here.
 */
export const exportLedgerToCSV = (ledger) => {
    const formattedData = ledger.map((item) => ({
        'Account Number': item.account_number,
        'Pay Period': item.pay_period,
        Amount: item.amount,
        Type: item.type === 'advance' ? 'Advance' : 'Service Fee',
        Date: item.created_at,
    }));

    exportToCSV(formattedData, 'ledger_export');
};

export const exportReconciliationToCSV = (reconciliation) => {
    const formattedData = reconciliation.map((item) => ({
        'Account Name': item.account_name,
        Email: item.email,
        'Amount Due': item.amount_due,
        'Days Past Due': item.days_past_due,
        'Pay Period': item.pay_period,
        'Scheduled For': item.scheduled_for,
        'Client ID': item.client_id,
    }));

    exportToCSV(formattedData, 'reconciliation_export');
};
